import { useMutation, useQueryClient } from '@tanstack/react-query';
import { del } from '@/shared/api/api-client';
import type { ArchivePatientsRequest } from '@/types/patient-api';

/**
 * DELETE /api/Patients/ArchivePatient with body { patientIds: [...] }.
 *
 * Intentional deviation from REST norm: the backend requires DELETE with a
 * JSON body (mirrors Flutter `client.delete(..., data: body)` in
 * `patients_list_data_source_impl.dart`). Some HTTP clients/proxies strip
 * bodies on DELETE — our `apiClient.del()` is built on `fetch`, which
 * preserves them. Do NOT "fix" this to POST without a backend rename; the
 * route only exists at this verb.
 */
export function useArchivePatients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: ArchivePatientsRequest) =>
      del<void>('/api/Patients/ArchivePatient', req),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['patients-list'] });
      // Detail views show an isArchived flag — invalidate each archived
      // patient's detail key so a separately-mounted detail page refetches.
      vars.patientIds.forEach((id) =>
        qc.invalidateQueries({ queryKey: ['patient', id] }),
      );
    },
  });
}
